import React from 'react';
import { Animated, Platform, View, ViewStyle } from 'react-native';
import { Cell } from '@tanstack/react-table';
import { createStyles } from '@/contexts/theme-context';
import { getLeftRightValues } from '@experiences/components/table/blocs/table';
import { useTableContext } from './blocs/table-context';

export interface TableCellProps<TData> {
  cell: Cell<TData, unknown>;
  children: React.ReactNode;
  style?: ViewStyle;
}

export const TableCell = <TData,>({
  cell,
  children,
  style,
}: TableCellProps<TData>) => {
  const [styles] = useStyles();
  const { cellPadding, columnWidths, pinnedScrollOffset } = useTableContext();

  const columnIndex = cell.column.getIndex();
  const columnPadding = Array.isArray(cellPadding)
    ? (cellPadding[columnIndex] ?? [0, 0])
    : cellPadding;
  const columnWidth = columnWidths[cell.column.id];

  const [paddingLeft, paddingRight] = getLeftRightValues(columnPadding);

  const pinnedOffset = cell.column.getIsPinned()
    ? cell.column.getStart('left')
    : undefined;

  const isPinned = typeof pinnedOffset === 'number';

  const cellStyles = [
    styles.container,
    { paddingLeft, paddingRight, width: columnWidth },
    ...(isPinned
      ? [
          styles.pinned,
          // Web uses left for position: sticky. On native, translateX handles
          // pinning; left: -1 closes a sub-pixel gap between the pinned cell's
          // right edge and the first scrolling cell.
          Platform.OS === 'web' ? { left: pinnedOffset } : { left: -1 },
        ]
      : []),
    style,
  ];

  // On native, use Animated.View with translateX to pin cells in place
  // during horizontal scrolling (web uses CSS position: sticky instead)
  if (isPinned && pinnedScrollOffset && Platform.OS !== 'web') {
    return (
      <Animated.View
        role="cell"
        style={[
          cellStyles,
          { transform: [{ translateX: pinnedScrollOffset }] },
        ]}
      >
        {children}
      </Animated.View>
    );
  }

  return (
    <View role="cell" style={cellStyles}>
      {children}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    backgroundColor: theme.sk.color.bg.primary.default,
    justifyContent: 'center',
    paddingVertical: theme.sk.spacing.spaceSmall,
    position: 'relative',
  },
  pinned: {
    ...Platform.select({
      // Sticky is a valid web style but not included in RN types
      web: { position: 'sticky' as ViewStyle['position'] },
    }),
    zIndex: 1,
  },
}));
